/**
 * Modu — auth + save dialog orchestrator
 * --------------------------------------
 * Companion to snippets/md-auth-modals.liquid + assets/md-auth.css. Handles
 * three concerns:
 *
 *   1. Login handoff. The store runs NEW customer accounts (hosted, passwordless),
 *      so the classic customers/login form is dead. Login now goes to
 *      window.MD_ACCOUNT.loginUrl (= Liquid routes.storefront_login_url), which
 *      returns the buyer to the page they were on before login. sessionStorage
 *      (md_return_url / md_pending_save) survives the same-tab cross-origin
 *      round-trip and is consumed on the destination page (see consumePendingSave
 *      in init), so pending-save resume still works. maybeRedirectFromAccount()
 *      is a legacy fallback for the old /account leg.
 *
 *   2. Auth modal. window.MD_Auth.openAuthModal({ pendingSave }) shows the
 *      sign-in / register tabs in #ModuAuthModal. The pendingSave payload (if
 *      provided) is stashed in sessionStorage so the post-auth /account-load
 *      redirect can resume the save.
 *
 *   3. Save dialog. window.MD_Auth.openSaveDialog({ name, profiles, onSave })
 *      shows a name + drawer-profile picker for logged-in customers and calls
 *      onSave({ name, drawerProfileId }) on submit.
 *
 * Loaded by main-login.liquid, main-register.liquid, main-account.liquid,
 * sections/drawer-image.liquid, and sections/main-order.liquid.
 */
(function () {
  'use strict';

  var STORAGE_RETURN = 'md_return_url';
  var STORAGE_PENDING_SAVE = 'md_pending_save';

  function getQueryParam(key) {
    try {
      return new URL(window.location.href).searchParams.get(key);
    } catch (_) {
      return null;
    }
  }

  function isLoggedIn() {
    var ctx = window.MD_FAV || {};
    var id = ctx.customerId;
    return !!(id && id !== 0 && id !== '0');
  }

  // New customer accounts: login is hosted + passwordless. window.MD_ACCOUNT.loginUrl
  // is Liquid's routes.storefront_login_url (layout/theme.liquid), which returns the
  // buyer to the page they were on before login — so we don't append return_to.
  // `ret` is kept for call-site compatibility; the same-tab sessionStorage
  // pending-save survives the round-trip and is consumed on the destination page.
  function loginUrlWithReturn(ret) {
    void ret;
    return (window.MD_ACCOUNT && window.MD_ACCOUNT.loginUrl) || '/account/login';
  }

  // -----------------------------------------------------------------------
  // 1. return_url round-tripping
  // -----------------------------------------------------------------------

  function captureReturnUrlFromQuery() {
    var ret = getQueryParam('return_url');
    if (ret) {
      try { sessionStorage.setItem(STORAGE_RETURN, ret); } catch (_) {}
    }
    // Also stamp it onto any `<input name="return_url">` on the page so a
    // future Shopify plugin/theme update that does honor the hidden input
    // gets the value too. Belt-and-braces.
    var stored = ret || readReturn();
    if (!stored) return;
    var inputs = document.querySelectorAll('input[name="return_url"]');
    inputs.forEach(function (input) {
      if (!input.value) input.value = stored;
    });
  }

  function readReturn() {
    try { return sessionStorage.getItem(STORAGE_RETURN); }
    catch (_) { return null; }
  }

  function clearReturn() {
    try {
      sessionStorage.removeItem(STORAGE_RETURN);
      sessionStorage.removeItem(STORAGE_PENDING_SAVE);
    } catch (_) {}
  }

  function maybeRedirectFromAccount() {
    // Only on /account (the post-login landing page), and only when logged in.
    if (!/^\/account\/?$/.test(window.location.pathname)) return;
    if (!isLoggedIn()) return;
    var ret = readReturn();
    if (!ret) return;
    // Mark pendingSave so the destination page can resume the save flow.
    var pending = null;
    try { pending = sessionStorage.getItem(STORAGE_PENDING_SAVE); } catch (_) {}
    // Don't clear yet — let the destination page consume STORAGE_PENDING_SAVE
    // and we clear STORAGE_RETURN after using it.
    try { sessionStorage.removeItem(STORAGE_RETURN); } catch (_) {}
    // Sanity: only follow same-origin paths to avoid redirect-leak.
    if (typeof ret !== 'string' || ret[0] !== '/') return;
    window.location.replace(ret);
    return; // pending is consumed at destination
    // (silence "unused" warning for `pending`)
    // eslint-disable-next-line no-unreachable
    void pending;
  }

  function setReturnTo(url) {
    try { sessionStorage.setItem(STORAGE_RETURN, url); } catch (_) {}
  }

  function setPendingSave(payload) {
    try { sessionStorage.setItem(STORAGE_PENDING_SAVE, JSON.stringify(payload)); }
    catch (_) {}
  }

  function consumePendingSave() {
    var raw = null;
    try {
      raw = sessionStorage.getItem(STORAGE_PENDING_SAVE);
      sessionStorage.removeItem(STORAGE_PENDING_SAVE);
    } catch (_) {}
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (_) { return null; }
  }

  // -----------------------------------------------------------------------
  // 2. Auth modal
  // -----------------------------------------------------------------------

  function getAuthModal() { return document.getElementById('ModuAuthModal'); }
  function getSaveModal() { return document.getElementById('ModuSaveDialog'); }

  function openAuthModal(opts) {
    opts = opts || {};
    var modal = getAuthModal();
    if (!modal) {
      // Fallback to a plain redirect to /account/login if the modal snippet
      // wasn't rendered on this page.
      var ret = (opts.returnUrl || (window.location.pathname + window.location.hash));
      setReturnTo(ret);
      if (opts.pendingSave) setPendingSave(opts.pendingSave);
      window.location.href = loginUrlWithReturn(ret);
      return;
    }
    // Stash return_url + pending-save BEFORE the user submits; if they submit
    // we want sessionStorage already populated so /account can redirect.
    var ret = (opts.returnUrl || (window.location.pathname + window.location.hash));
    setReturnTo(ret);
    if (opts.pendingSave) setPendingSave(opts.pendingSave);

    // Populate hidden return_url inputs inside the modal.
    modal.querySelectorAll('input[data-md-return-url]').forEach(function (input) {
      input.value = ret;
    });

    modal.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';
    var firstInput = modal.querySelector('[data-md-auth-panel="login"] input[name="customer[email]"]');
    if (firstInput) firstInput.focus();
  }

  function closeAuthModal() {
    var modal = getAuthModal();
    if (!modal) return;
    modal.setAttribute('hidden', '');
    document.body.style.overflow = '';
    // Don't clear sessionStorage — user may reopen and submit later.
  }

  function switchAuthTab(target, modal) {
    modal = modal || getAuthModal();
    if (!modal) return;
    modal.querySelectorAll('[data-md-auth-tab]').forEach(function (btn) {
      var active = btn.getAttribute('data-md-auth-tab') === target;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    modal.querySelectorAll('[data-md-auth-panel]').forEach(function (panel) {
      var active = panel.getAttribute('data-md-auth-panel') === target;
      if (active) panel.removeAttribute('hidden');
      else panel.setAttribute('hidden', '');
    });
  }

  function bindAuthModal() {
    var modal = getAuthModal();
    if (!modal || modal.dataset.mdBound) return;
    modal.dataset.mdBound = '1';

    modal.addEventListener('click', function (e) {
      if (e.target.closest('[data-md-auth-close]')) {
        closeAuthModal();
      }
      var tab = e.target.closest('[data-md-auth-tab]');
      if (tab) {
        switchAuthTab(tab.getAttribute('data-md-auth-tab'), modal);
      }
    });

    // Esc key closes whichever modal is currently visible. Cheap a11y win.
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var auth = getAuthModal();
      var save = getSaveModal();
      if (auth && !auth.hasAttribute('hidden')) closeAuthModal();
      else if (save && !save.hasAttribute('hidden')) closeSaveDialog();
    });

    // Forms inside post normally to Shopify (full-page navigation).
    // They already have hidden return_url inputs which we set on open.
  }

  // -----------------------------------------------------------------------
  // 3. Save dialog
  // -----------------------------------------------------------------------

  function openSaveDialog(opts) {
    opts = opts || {};
    var modal = getSaveModal();
    if (!modal) {
      // Fallback: simple prompt() so the flow still works on pages that don't
      // include the snippet.
      var fallbackName = window.prompt('Name this design (optional):', opts.name || '');
      if (fallbackName === null) return;
      if (typeof opts.onSave === 'function') {
        opts.onSave({ name: fallbackName || '', drawerProfileId: null });
      }
      return;
    }

    var nameInput = modal.querySelector('#ModuSaveName');
    if (nameInput) nameInput.value = opts.name || '';

    // Wire profile picker if profiles supplied (Tier 2).
    var profileField = modal.querySelector('[data-md-save-profile-field]');
    var profileSelect = modal.querySelector('[data-md-save-profile-select]');
    if (profileSelect) {
      // Remove all but the first (none) option.
      while (profileSelect.options.length > 1) profileSelect.remove(1);
      var profiles = Array.isArray(opts.profiles) ? opts.profiles : [];
      profiles.forEach(function (p) {
        var opt = document.createElement('option');
        opt.value = p.id;
        opt.textContent = p.name + (p.lengthMM && p.widthMM
          ? '  (' + p.lengthMM + ' × ' + p.widthMM + ' mm)'
          : '');
        profileSelect.appendChild(opt);
      });
      if (opts.drawerProfileId) profileSelect.value = opts.drawerProfileId;
      if (profileField) {
        if (profiles.length) profileField.removeAttribute('hidden');
        else profileField.setAttribute('hidden', '');
      }
    }

    modal.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';
    if (nameInput) nameInput.focus();

    // Bind submit + close (idempotent).
    if (!modal.dataset.mdBound) {
      modal.dataset.mdBound = '1';
      modal.addEventListener('click', function (e) {
        if (e.target.closest('[data-md-save-close]')) {
          closeSaveDialog();
          if (typeof modal._mdOnCancel === 'function') modal._mdOnCancel();
        }
      });
      var form = modal.querySelector('[data-md-save-form]');
      if (form) {
        form.addEventListener('submit', function (e) {
          e.preventDefault();
          var name = (nameInput && nameInput.value || '').trim();
          var drawerProfileId = profileSelect ? (profileSelect.value || null) : null;
          var handler = modal._mdOnSave;
          closeSaveDialog();
          if (typeof handler === 'function') {
            handler({ name: name, drawerProfileId: drawerProfileId });
          }
        });
      }
    }

    modal._mdOnSave = opts.onSave;
    modal._mdOnCancel = opts.onCancel;
  }

  function closeSaveDialog() {
    var modal = getSaveModal();
    if (!modal) return;
    modal.setAttribute('hidden', '');
    document.body.style.overflow = '';
  }

  // -----------------------------------------------------------------------
  // Init
  // -----------------------------------------------------------------------

  function init() {
    captureReturnUrlFromQuery();
    bindAuthModal();
    maybeRedirectFromAccount();
    // Resume pending-save if the destination page asked for it.
    var pending = consumePendingSave();
    if (pending && isLoggedIn()) {
      window.dispatchEvent(new CustomEvent('md:auth:resume-save', { detail: pending }));
    }
  }

  // Public API
  window.MD_Auth = {
    openAuthModal: openAuthModal,
    closeAuthModal: closeAuthModal,
    openSaveDialog: openSaveDialog,
    closeSaveDialog: closeSaveDialog,
    setReturnTo: setReturnTo,
    setPendingSave: setPendingSave,
    consumePendingSave: consumePendingSave,
    clearReturn: clearReturn
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
